import { formatCurrency, formatDate } from "@angular/common";
import { Component, LOCALE_ID, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { AccountDetailDrilldownViewModel, ActivityDetail, EncumbranceDetail, ReserveDetail, } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AccountDetailDrilldownService } from "../../services";
import { ViewOnlyInfoCardDialogComponent, ViewOnlyInfoCardDialogData } from "./viewOnlyInfoCardDialog.component";

type DetailModel = Partial<ActivityDetail & EncumbranceDetail & ReserveDetail>;

export type AccountDetailType = "Activity" | "Encumbrance" | "Reserve";

type DetailColumn = "PostDate" | "SourceTransaction" | "Amount" | "Module" | "Description" | "EncumbranceDate"
    | "Number" | "RequisitionNumber";

type FilterState<T extends string = string> = Partial<Record<T, string>>;

export interface AccountDetailDialogData
{
    readonly fetch: AccountDetailDrilldownViewModel;
    readonly type: AccountDetailType;
}

@Component({
    selector: "account-detail-dialog-component",
    templateUrl: "./accountDetailDialog.tmpl.html",
    styleUrls: [
        "./accountDetailDialog.component.scss"
    ],
    standalone: false
})
export class AccountDetailDialogComponent implements OnInit, OnDestroy
{
    private readonly matDialog = inject(MatDialog);
    private readonly data = inject<AccountDetailDialogData>(MAT_DIALOG_DATA);
    private readonly $accountDetailDrilldownService = inject(AccountDetailDrilldownService);
    private readonly locale = inject(LOCALE_ID);

    private readonly destructor$ = new Subject<void>();
    private readonly filterState: FilterState<DetailColumn> = {};

    @ViewChild(MatPaginator)
    readonly paginator: MatPaginator;

    readonly tableData: MatTableDataSource<DetailModel>;
    readonly formGroup: FormGroup;

    isLoaded: boolean;
    columnNames: ReadonlyArray<DetailColumn>;

    private _pageSizes: ReadonlyArray<number>;
    private _initialPageSize: number;

    constructor()
    {
        this.tableData = new MatTableDataSource<DetailModel>();
        this.tableData.filterPredicate = (data: DetailModel, filter: string): boolean =>
        {
            const state = JSON.parse(filter) as FilterState<DetailColumn>;
            return Object.entries(state)
                .reduce(
                    (result: boolean, [column, filterValue]: [string, string]): boolean =>
                    {
                        if (!result || !filterValue)
                            return result;

                        const value: string | undefined = data[column]?.toString().toLowerCase();
                        if (!value || value.indexOf(filterValue) === -1)
                        {
                            return false;
                        }
                        return true;
                    },
                    true);
        };

        this.formGroup = new FormGroup({
            "postDateFilter": LgdFormControlFactory.text(),
            "sourceTransactionFilter": LgdFormControlFactory.text(),
            "moduleFilter": LgdFormControlFactory.text(),
            "descriptionFilter": LgdFormControlFactory.text(),
            "encumbranceDateFilter": LgdFormControlFactory.text(),
            "numberFilter": LgdFormControlFactory.text(),
            "requisitionNumberFilter": LgdFormControlFactory.text(),
            "amountFilter": LgdFormControlFactory.text(),
        });
    }

    private get fetch(): AccountDetailDrilldownViewModel { return this.data.fetch; }

    get type(): AccountDetailType { return this.data.type; }

    get pageSizes(): ReadonlyArray<number> { return this._pageSizes || [5, 10, 20]; }
    set pageSizes(value: ReadonlyArray<number>) { this._pageSizes = value; }

    get initialPageSize(): number { return this._initialPageSize || this.pageSizes[0]; }
    set initialPageSize(value: number) { this._initialPageSize = value; }

    private get postDateFilterControl(): AbstractControl<string> { return this.formGroup.get("postDateFilter"); }
    private get sourceTransactionFilterControl(): AbstractControl<string> { return this.formGroup.get("sourceTransactionFilter"); }
    private get moduleFilterControl(): AbstractControl<string> { return this.formGroup.get("moduleFilter"); }
    private get descriptionFilterControl(): AbstractControl<string> { return this.formGroup.get("descriptionFilter"); }
    private get encumbranceDateFilterControl(): AbstractControl<string> { return this.formGroup.get("encumbranceDateFilter"); }
    private get numberFilterControl(): AbstractControl<string> { return this.formGroup.get("numberFilter"); }
    private get requisitionNumberFilterControl(): AbstractControl<string> { return this.formGroup.get("requisitionNumberFilter"); }
    private get amountFilterControl(): AbstractControl<string> { return this.formGroup.get("amountFilter"); }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        this.fetchDetail().then(() => self.isLoaded = true);

        subscribeToValueChanges(this.postDateFilterControl, (newValue: string) =>
        {
            this.filterState.PostDate = newValue.toLowerCase();

            this.updateTableFilter();
        });

        subscribeToValueChanges(this.sourceTransactionFilterControl, (newValue: string) =>
        {
            this.filterState.SourceTransaction = newValue.toLowerCase();

            this.updateTableFilter();
        });

        subscribeToValueChanges(this.moduleFilterControl, (newValue: string) =>
        {
            this.filterState.Module = newValue.toLowerCase();

            this.updateTableFilter();
        });

        subscribeToValueChanges(this.descriptionFilterControl, (newValue: string) =>
        {
            this.filterState.Description = newValue.toLowerCase();

            this.updateTableFilter();
        });

        subscribeToValueChanges(this.encumbranceDateFilterControl, (newValue: string) =>
        {
            this.filterState.RequisitionNumber = newValue.toLowerCase();

            this.updateTableFilter();
        });

        subscribeToValueChanges(this.numberFilterControl, (newValue: string) =>
        {
            this.filterState.Number = newValue.toLowerCase();

            this.updateTableFilter();
        });

        subscribeToValueChanges(this.requisitionNumberFilterControl, (newValue: string) =>
        {
            this.filterState.RequisitionNumber = newValue.toLowerCase();

            this.updateTableFilter();
        });

        subscribeToValueChanges(this.amountFilterControl, (newValue: string) =>
        {
            this.filterState.Amount = newValue.toLowerCase();

            this.updateTableFilter();
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    private updateTableFilter(): void
    {
        this.tableData.filter = JSON.stringify(this.filterState);
    }

    private fetchDetail(): Promise<void>
    {
        switch (this.type)
        {
            case "Activity":
                return this.$accountDetailDrilldownService.GetActivity(this.fetch)
                    .then((result: Array<ActivityDetail>) =>
                    {
                        const useModule = result.filter(x => x.Module).length > 0;
                        this.columnNames = useModule
                            ? ["PostDate", "SourceTransaction", "Amount", "Module", "Description"]
                            : ["PostDate", "SourceTransaction", "Amount", "Description"];

                        this.tableData.data = result;
                    });
            case "Encumbrance":
                return this.$accountDetailDrilldownService.GetEncumbrance(this.fetch)
                    .then((result: Array<EncumbranceDetail>) =>
                    {
                        this.columnNames = ["EncumbranceDate", "Number", "Amount", "Description"];

                        this.tableData.data = result;
                    });
            case "Reserve":
                return this.$accountDetailDrilldownService.GetReserve(this.fetch)
                    .then((result: Array<ReserveDetail>) =>
                    {
                        this.columnNames = ["PostDate", "RequisitionNumber", "Amount", "Description"];

                        this.tableData.data = result;
                    });
        }
    }

    goToInvoice(row: DetailModel): void
    {
        if (!row.InvoiceId)
        {
            return;
        }

        // TODO - dialogs shouldn't open dialogs, we were supposed to stop doing this forever ago!
        const dialogConfig: MatDialogConfig<ViewOnlyInfoCardDialogData> = {
            data: {
                cardProviderId: "Invoice.InfoCardProvider",
                itemId: row.InvoiceId
            }
        };
        this.matDialog.open(ViewOnlyInfoCardDialogComponent, dialogConfig);
    }

    hasInvoice(row: DetailModel): boolean
    {
        return (row.Invoice && row.Invoice !== "0") || !!row.InvoiceId;
    }

    getTextForColumn(value: string): string
    {
        switch (value)
        {
            case "EncumbranceDate":
                return "Date";
            case "ReserveAmount":
                return "Amount";
            default:
                return value.replace(/([a-z\d])([A-Z])/g, "$1 $2");
        }
    }

    getAmountTooltip(row: DetailModel): string
    {
        return `Amount: ${formatCurrency(row.Amount, this.locale, "$")}`;
    }

    getRequisitionNumberTooltip(row: DetailModel): string
    {
        return `Number: ${row.RequisitionNumber}`;
    }

    getNumberTooltip(row: DetailModel): string
    {
        return `Number: ${row.Number}`;
    }

    getEncumbranceDateTooltip(row: DetailModel): string
    {
        return `Post Date: ${formatDate(row.EncumbranceDate, "MM/dd/yyyy", this.locale)}`;
    }

    getPostDateTooltip(row: DetailModel): string
    {
        return `Post Date: ${formatDate(row.PostDate, "MM/dd/yyyy", this.locale)}`;
    }

    getDescriptionTooltip(row: DetailModel): string
    {
        return `Description: ${row.Description}`;
    }

    getModuleTooltip(row: DetailModel): string
    {
        return `Module: ${row.Module}`;
    }

    getSourceTransactionTooltip(row: DetailModel): string
    {
        return `Source Transaction: ${this.getTextForColumn(row.SourceTransaction)}`;
    }
}
